import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Container, Form, Button, Alert, Card, Row, Col, Spinner, Badge, ListGroup, Modal, Table } from 'react-bootstrap';
import { useAuth } from '../context/AuthContext';
import { usePageTitle } from '../context/PageTitleContext';
import { useNavigate, useParams } from 'react-router-dom';
import { prepJpeg } from '../utils/imagePrep';

const IMAGE_TYPES = ['exterior', 'interior', 'ambiance', 'logo', 'other'];

// Creator editing (#71 / #93): lets the user who added a restaurant edit it —
// core fields plus FULL location management (add/edit any location, switch which
// is main), not just the main one. Restaurant fields save via PUT /restaurants/{id};
// each location saves via PUT /restaurants/locations/{locId} (existing) or
// POST /restaurants/{id}/locations/ (new). The backend enforces the single-main
// invariant, so marking one location main demotes the others. Deleting a location
// is admin-only (owners hide, not delete — see #155), so no delete control here.

const COUNTRIES = [
  'United States', 'Canada', 'Bangladesh', 'United Kingdom', 'Australia', 'India',
  'Mexico', 'France', 'Germany', 'Italy', 'Spain', 'Japan', 'China', 'South Korea',
  'Thailand', 'Brazil', 'Other',
];

const blankLocation = (key, isMain = false) => ({
  key,
  id: null, // null = not yet saved
  name: '',
  address: '',
  unit: '',
  city: '',
  state: '',
  zip_code: '',
  country: 'United States',
  phone: '',
  is_main_location: isMain,
  // #166/#157: Google-derived, per location. Carried in card state so the
  // Places picker can diff against what's already there before overwriting.
  latitude: null,
  longitude: null,
  google_place_id: null,
  monday_hours: '',
  tuesday_hours: '',
  wednesday_hours: '',
  thursday_hours: '',
  friday_hours: '',
  saturday_hours: '',
  sunday_hours: '',
});

// Default per-card Places search state. Module-level so it's a stable reference
// and every merge starts from the same shape.
const EMPTY_PLACE_SEARCH = { query: '', predictions: [], loading: false, error: '' };

// Fields a Places match can supply, in display order. Drives both the diff
// preview and what gets applied on confirm (#166/#157).
const PLACE_FIELDS = [
  ['address', 'Address'],
  ['city', 'City'],
  ['state', 'State'],
  ['zip_code', 'ZIP'],
  ['latitude', 'Latitude'],
  ['longitude', 'Longitude'],
  ['monday_hours', 'Mon hours'],
  ['tuesday_hours', 'Tue hours'],
  ['wednesday_hours', 'Wed hours'],
  ['thursday_hours', 'Thu hours'],
  ['friday_hours', 'Fri hours'],
  ['saturday_hours', 'Sat hours'],
  ['sunday_hours', 'Sun hours'],
];

// Primary first, then by display order. is_primary arrives as bool (restaurant
// schema) or int (the /images endpoint) — Boolean() normalises both.
const sortImages = (imgs) =>
  [...imgs].sort(
    (a, b) => (Boolean(b.is_primary) - Boolean(a.is_primary)) || (a.display_order - b.display_order)
  );

function EditRestaurantForm() {
  const { id } = useParams();
  const { setPageTitle } = usePageTitle();
  const { token, user, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    website_url: '',
    menu_url: '',
    flickr_album_url: '',
    category_ids: [],
    cuisine_ids: [],
  });

  const [locations, setLocations] = useState([]);
  const [images, setImages] = useState([]);
  const [imageBusy, setImageBusy] = useState(false);
  const [imageError, setImageError] = useState('');
  const [newCaption, setNewCaption] = useState('');
  const [newImageType, setNewImageType] = useState('other');
  const [newImageLocationId, setNewImageLocationId] = useState(''); // '' = all locations
  const [categories, setCategories] = useState([]);
  const [cuisines, setCuisines] = useState([]);
  const [canEdit, setCanEdit] = useState(null); // null = still loading/authz unknown
  const [loadError, setLoadError] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [initializing, setInitializing] = useState(true);

  // Monotonic key source for new (unsaved) location cards, so React keys stay stable.
  const newKey = useRef(0);
  const nextKey = () => `new-${newKey.current++}`;

  // #166/#157: per-card Places search state, keyed by location card key, and the
  // single staged match awaiting confirmation. One session token for the whole
  // edit session keeps autocomplete + details billed as one Google session.
  const [placeState, setPlaceState] = useState({});
  const [pendingMatch, setPendingMatch] = useState(null);
  const sessionTokenRef = useRef(null);
  if (!sessionTokenRef.current) {
    sessionTokenRef.current =
      (window.crypto?.randomUUID?.() || `sess-${Date.now()}-${Math.random().toString(16).slice(2)}`);
  }
  const sessionToken = sessionTokenRef.current;

  useEffect(() => {
    setPageTitle('Edit Restaurant');
  }, [setPageTitle]);

  const authHeaders = useCallback(
    () => (token ? { Authorization: `Bearer ${token}` } : {}),
    [token]
  );

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setInitializing(true);
      setLoadError('');
      try {
        const [catRes, cuiRes, resRes] = await Promise.all([
          fetch(`${API_BASE_URL}/restaurants/categories/`),
          fetch(`${API_BASE_URL}/cuisines/`),
          fetch(`${API_BASE_URL}/restaurants/${id}`, { headers: authHeaders() }),
        ]);

        if (!resRes.ok) {
          throw new Error(resRes.status === 404 ? 'Restaurant not found.' : 'Could not load this restaurant.');
        }

        const restaurant = await resRes.json();
        if (cancelled) return;

        if (catRes.ok) {
          const data = await catRes.json();
          setCategories(data.sort((a, b) => a.name.localeCompare(b.name)));
        }
        if (cuiRes.ok) {
          const data = await cuiRes.json();
          setCuisines(data.sort((a, b) => a.name.localeCompare(b.name)));
        }

        // Authorization: only the creator (owner) or an admin may edit.
        const isOwner = user && restaurant.created_by_user_id === user.id;
        const isAdmin = Boolean(user && user.is_superuser);
        if (!(isOwner || isAdmin)) {
          setCanEdit(false);
          setInitializing(false);
          return;
        }
        setCanEdit(true);

        const loaded = (restaurant.locations || []).map((l) => ({
          key: `loc-${l.id}`,
          id: l.id,
          name: l.name || '',
          address: l.address || '',
          unit: l.unit || '',
          city: l.city || '',
          state: l.state || '',
          zip_code: l.zip_code || '',
          country: l.country || 'United States',
          phone: l.phone || '',
          is_main_location: Boolean(l.is_main_location),
          // #166/#157: needed so the picker can show what it would change.
          latitude: l.latitude ?? null,
          longitude: l.longitude ?? null,
          google_place_id: l.google_place_id ?? null,
          monday_hours: l.monday_hours || '',
          tuesday_hours: l.tuesday_hours || '',
          wednesday_hours: l.wednesday_hours || '',
          thursday_hours: l.thursday_hours || '',
          friday_hours: l.friday_hours || '',
          saturday_hours: l.saturday_hours || '',
          sunday_hours: l.sunday_hours || '',
        }));
        // Guarantee exactly one main: if none is flagged, promote the first.
        if (loaded.length > 0 && !loaded.some((l) => l.is_main_location)) {
          loaded[0].is_main_location = true;
        }
        // No locations yet → start with one blank main card (matches the old behaviour).
        setLocations(loaded.length > 0 ? loaded : [blankLocation(nextKey(), true)]);
        setImages(sortImages(restaurant.images || []));

        setFormData({
          name: restaurant.name || '',
          description: restaurant.description || '',
          website_url: restaurant.website_url || '',
          menu_url: restaurant.menu_url || '',
          flickr_album_url: restaurant.flickr_album_url || '',
          category_ids: (restaurant.categories || []).map((c) => c.id),
          cuisine_ids: (restaurant.cuisines || []).map((c) => c.id),
        });
      } catch (err) {
        if (!cancelled) setLoadError(err.message);
      } finally {
        if (!cancelled) setInitializing(false);
      }
    };

    if (isAuthenticated) load();
    else setInitializing(false);

    return () => { cancelled = true; };
  }, [id, isAuthenticated, user, API_BASE_URL, authHeaders]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleMultiSelect = (e, fieldName) => {
    const selected = Array.from(e.target.selectedOptions, (o) => parseInt(o.value));
    setFormData((prev) => ({ ...prev, [fieldName]: selected }));
  };

  const updateLocation = (key, field, value) => {
    setLocations((prev) => prev.map((l) => (l.key === key ? { ...l, [field]: value } : l)));
  };

  const setMainLocation = (key) => {
    setLocations((prev) => prev.map((l) => ({ ...l, is_main_location: l.key === key })));
  };

  // ---- #166/#157: Google Places picker, per location card -------------------
  // Picking NEVER writes straight through. It builds a diff and waits for an
  // explicit confirm, because Google can return a relocated or otherwise wrong
  // match (the Cafe Luna case the backend comments call out) and a silent
  // overwrite would destroy a hand-corrected address.

  const placeSearch = (key) => placeState[key] || EMPTY_PLACE_SEARCH;

  // Must merge from `prev`, NOT from the closed-over placeState: two calls in the
  // same tick (as searchPlaces makes) would otherwise both build off the same
  // stale value and the last would clobber the first — which wiped `query` on
  // every keystroke and made the input look dead.
  const setPlaceSearch = (key, patch) =>
    setPlaceState((prev) => ({
      ...prev,
      [key]: { ...(prev[key] || EMPTY_PLACE_SEARCH), ...patch },
    }));

  const searchPlaces = async (key, text, loc) => {
    // Single update for the short-query case — setting the query and clearing
    // predictions in one patch rather than two sequential ones.
    if (!text || text.trim().length < 3) {
      setPlaceSearch(key, { query: text, error: '', predictions: [] });
      return;
    }
    setPlaceSearch(key, { query: text, error: '' });
    try {
      setPlaceSearch(key, { loading: true });
      // #246: rank suggestions around the location being edited. Sending no
      // coordinates is not neutral — Google then ranks by the IP of whoever
      // called it, and that is our backend, so every admin everywhere got
      // suggestions near New Jersey.
      //
      // The card's own coordinates beat the browser's here: you edit a
      // restaurant from your desk far more often than from its doorstep, and
      // this card already holds where the place actually is (#166/#157). It
      // also means no geolocation prompt on this screen at all. Cards with no
      // coordinates yet fall through to unbiased, exactly as before.
      const res = await fetch(`${API_BASE_URL}/api/google-maps/autocomplete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({
          input: text, types: 'establishment', session_token: sessionToken,
          ...(loc?.latitude != null && loc?.longitude != null
            ? { latitude: loc.latitude, longitude: loc.longitude }
            : {}),
        }),
      });
      if (!res.ok) throw new Error('Place lookup failed');
      const data = await res.json();
      setPlaceSearch(key, { predictions: data.predictions || [] });
    } catch (err) {
      setPlaceSearch(key, { error: err.message, predictions: [] });
    } finally {
      setPlaceSearch(key, { loading: false });
    }
  };

  /// Fetch details for the picked place and stage a diff against the card.
  const stagePlaceMatch = async (key, placeId, label) => {
    try {
      setPlaceSearch(key, { loading: true, error: '', predictions: [] });
      const res = await fetch(`${API_BASE_URL}/api/google-maps/place-details`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ place_id: placeId, session_token: sessionToken, include_hours: true }),
      });
      if (!res.ok) throw new Error('Could not load that place');
      const d = await res.json();
      // opening_hours arrives pre-mapped as { monday_hours, ... } — the backend
      // runs parse_opening_hours for us.
      const h = d.opening_hours || {};
      const proposed = {
        address: d.address || '',
        city: d.city || '',
        state: d.state || '',
        zip_code: d.zip_code || '',
        latitude: d.latitude ?? null,
        longitude: d.longitude ?? null,
        monday_hours: h.monday_hours || '',
        tuesday_hours: h.tuesday_hours || '',
        wednesday_hours: h.wednesday_hours || '',
        thursday_hours: h.thursday_hours || '',
        friday_hours: h.friday_hours || '',
        saturday_hours: h.saturday_hours || '',
        sunday_hours: h.sunday_hours || '',
      };
      const current = locations.find((l) => l.key === key) || {};
      const changes = PLACE_FIELDS
        .map(([field, label2]) => ({
          field,
          label: label2,
          from: current[field] ?? null,
          to: proposed[field] ?? null,
        }))
        // Only surface real differences; unchanged rows are noise in the preview.
        .filter((c) => String(c.from ?? '') !== String(c.to ?? ''));
      setPendingMatch({ key, placeId, label, proposed, changes });
    } catch (err) {
      setPlaceSearch(key, { error: err.message });
    } finally {
      setPlaceSearch(key, { loading: false });
    }
  };

  const applyPendingMatch = () => {
    if (!pendingMatch) return;
    const { key, placeId, proposed } = pendingMatch;
    setLocations((prev) =>
      prev.map((l) => (l.key === key ? { ...l, ...proposed, google_place_id: placeId } : l))
    );
    setPendingMatch(null);
    setPlaceSearch(key, { query: '', predictions: [] });
  };

  const addLocation = () => {
    setLocations((prev) => [...prev, blankLocation(nextKey(), false)]);
  };

  // Only unsaved (id === null) cards can be removed from the form. Existing
  // locations are admin-delete-only (#155), so they have no remove control.
  const removeNewLocation = (key) => {
    setLocations((prev) => {
      const next = prev.filter((l) => l.key !== key);
      // If we removed the main, promote the first remaining so there's always a main.
      if (next.length > 0 && !next.some((l) => l.is_main_location)) {
        next[0] = { ...next[0], is_main_location: true };
      }
      return next;
    });
  };

  const refreshImages = async () => {
    // #248: owner-aware endpoint — without the token an owner editing their own
    // PRIVATE restaurant gets a 404 and sees an empty image list.
    const res = await fetch(`${API_BASE_URL}/restaurants/${id}/images`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (res.ok) {
      const data = await res.json();
      setImages(sortImages(data.images || []));
    }
  };

  // Upload a photo: prep (downscale + EXIF strip) → presign → PUT to R2 → record.
  // Immediate (not part of the form's Save). The first photo becomes primary.
  const handleImageUpload = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = ''; // let the same file be re-picked later
    if (!file) return;
    setImageError('');
    setImageBusy(true);
    try {
      const blob = await prepJpeg(file);
      const presignRes = await fetch(`${API_BASE_URL}/restaurants/${id}/images/presign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ content_type: 'image/jpeg', ext: 'jpg' }),
      });
      if (!presignRes.ok) {
        const d = await presignRes.json().catch(() => ({}));
        throw new Error(d.detail || 'Could not start the upload');
      }
      const { upload_url, storage_key } = await presignRes.json();

      const putRes = await fetch(upload_url, {
        method: 'PUT',
        headers: { 'Content-Type': 'image/jpeg' },
        body: blob,
      });
      if (!putRes.ok) throw new Error('The photo upload failed');

      const recordRes = await fetch(`${API_BASE_URL}/restaurants/${id}/images/upload`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({
          storage_key,
          content_type: 'image/jpeg',
          caption: newCaption.trim() || null,
          image_type: newImageType,
          location_id: newImageLocationId ? parseInt(newImageLocationId, 10) : null,
          is_primary: images.length === 0, // first photo becomes primary
        }),
      });
      if (!recordRes.ok) {
        const d = await recordRes.json().catch(() => ({}));
        throw new Error(d.detail || 'The photo was uploaded but could not be saved');
      }
      setNewCaption('');
      await refreshImages();
    } catch (err) {
      setImageError(err.message);
    } finally {
      setImageBusy(false);
    }
  };

  const handleSetPrimary = async (imageId) => {
    setImageError('');
    setImageBusy(true);
    try {
      const put = (imgId, isPrimary) =>
        fetch(`${API_BASE_URL}/restaurants/images/${imgId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', ...authHeaders() },
          body: JSON.stringify({ is_primary: isPrimary }),
        });
      const res = await put(imageId, true);
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.detail || 'Could not set the primary photo');
      }
      // The backend PUT doesn't demote the old primary (no single-primary
      // enforcement server-side yet — a follow-up, mirroring #93 single-main), so
      // demote the others here to keep exactly one primary.
      await Promise.all(
        images.filter((img) => img.id !== imageId && Boolean(img.is_primary)).map((img) => put(img.id, false))
      );
      await refreshImages();
    } catch (err) {
      setImageError(err.message);
    } finally {
      setImageBusy(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!formData.name.trim()) {
      setError('Restaurant name is required');
      return;
    }
    if (locations.length === 0) {
      setError('Add at least one location');
      return;
    }
    if (locations.some((l) => !l.city.trim())) {
      setError('Every location needs a city');
      return;
    }
    if (!locations.some((l) => l.is_main_location)) {
      setError('Pick a main location');
      return;
    }

    setLoading(true);
    try {
      // 1) Restaurant-level fields. is_approved is intentionally NOT sent — the
      //    backend pins it for non-admins anyway (no self-approval, #71).
      const restRes = await fetch(`${API_BASE_URL}/restaurants/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({
          name: formData.name,
          description: formData.description || null,
          website_url: formData.website_url || null,
          menu_url: formData.menu_url || null,
          flickr_album_url: formData.flickr_album_url || null,
          category_ids: formData.category_ids,
          cuisine_ids: formData.cuisine_ids,
        }),
      });
      if (!restRes.ok) {
        const data = await restRes.json().catch(() => ({}));
        throw new Error(data.detail || 'Failed to update restaurant');
      }

      // 2) Each location. The backend enforces single-main, so only the one
      //    marked main sends is_main_location=true and it demotes the others.
      for (const loc of locations) {
        const payload = {
          name: loc.name || null,
          address: loc.address || '',
          unit: loc.unit || '',
          city: loc.city,
          state: loc.state || '',
          zip_code: loc.zip_code || '',
          country: loc.country || 'United States',
          phone: loc.phone || '',
          is_main_location: loc.is_main_location,
          // #166/#157: only sent when this card actually has them (a location
          // never touched by the picker keeps whatever the server already holds
          // rather than being blanked by empty strings).
          ...(loc.google_place_id ? { google_place_id: loc.google_place_id } : {}),
          ...(loc.latitude != null ? { latitude: loc.latitude } : {}),
          ...(loc.longitude != null ? { longitude: loc.longitude } : {}),
          ...Object.fromEntries(
            ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']
              .map((d) => [`${d}_hours`, loc[`${d}_hours`]])
              .filter(([, v]) => v)
          ),
        };
        const res = loc.id
          ? await fetch(`${API_BASE_URL}/restaurants/locations/${loc.id}`, {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json', ...authHeaders() },
              body: JSON.stringify(payload),
            })
          : await fetch(`${API_BASE_URL}/restaurants/${id}/locations/`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', ...authHeaders() },
              body: JSON.stringify(payload),
            });
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          const which = loc.name || loc.city || 'a location';
          throw new Error(data.detail || `Restaurant saved, but ${which} could not be saved`);
        }
      }

      setSuccess('Changes saved. Your edits are live now; an admin will see them in the review queue. Redirecting…');
      setTimeout(() => navigate(`/restaurant/${id}`), 2000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <Container className="my-5">
        <Alert variant="info">
          Please <a href="/login">login</a> to edit a restaurant.
        </Alert>
      </Container>
    );
  }

  if (initializing) {
    return (
      <Container className="my-5 text-center">
        <Spinner animation="border" /> <span className="ms-2">Loading…</span>
      </Container>
    );
  }

  if (loadError) {
    return (
      <Container className="my-5">
        <Alert variant="danger">{loadError}</Alert>
        <Button variant="secondary" onClick={() => navigate(`/restaurant/${id}`)}>← Back</Button>
      </Container>
    );
  }

  if (canEdit === false) {
    return (
      <Container className="my-5">
        <Alert variant="warning">
          Only the person who added this restaurant (or an admin) can edit it.
        </Alert>
        <Button variant="secondary" onClick={() => navigate(`/restaurant/${id}`)}>← Back</Button>
      </Container>
    );
  }

  return (
    <Container className="my-5">
      <Card>
        <Card.Body>
          <h2 className="mb-4">Edit Restaurant</h2>
          <p className="text-muted mb-4">
            Update the details below. Your changes go live immediately and are flagged for an admin to review.
          </p>

          {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}
          {success && <Alert variant="success" dismissible onClose={() => setSuccess('')}>{success}</Alert>}

          <Form onSubmit={handleSubmit}>
            <h5 className="mb-3">Basic Information</h5>
            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Restaurant Name <span className="text-danger">*</span></Form.Label>
                  <Form.Control type="text" name="name" value={formData.name} onChange={handleChange} required />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control as="textarea" rows={3} name="description" value={formData.description} onChange={handleChange} placeholder="Tell us about this restaurant..." />
              <Form.Text className="text-muted">
                You can add links and <strong>bold</strong> text — paste a URL, or use{' '}
                <code>[label](https://…)</code> and <code>**bold**</code>.
              </Form.Text>
            </Form.Group>

            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Website URL</Form.Label>
                  <Form.Control type="url" name="website_url" value={formData.website_url} onChange={handleChange} placeholder="https://example.com" />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Menu URL</Form.Label>
                  <Form.Control type="url" name="menu_url" value={formData.menu_url} onChange={handleChange} placeholder="https://example.com/menu" />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Flickr Album URL</Form.Label>
                  <Form.Control type="url" name="flickr_album_url" value={formData.flickr_album_url} onChange={handleChange} placeholder="https://flickr.com/photos/..." />
                </Form.Group>
              </Col>
            </Row>

            <div className="d-flex align-items-center justify-content-between mb-3 mt-4">
              <h5 className="mb-0">Locations</h5>
              <Button variant="outline-primary" size="sm" onClick={addLocation} type="button">
                + Add location
              </Button>
            </div>

            {locations.map((loc) => (
              <Card key={loc.key} className="mb-3 border">
                <Card.Body>
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <Form.Check
                      type="radio"
                      name="mainLocation"
                      id={`main-${loc.key}`}
                      label={
                        <span>
                          Main location{' '}
                          {loc.is_main_location && <Badge bg="primary" className="ms-1">Main</Badge>}
                        </span>
                      }
                      checked={loc.is_main_location}
                      onChange={() => setMainLocation(loc.key)}
                    />
                    {loc.id === null && (
                      <Button variant="link" size="sm" className="text-danger p-0" type="button" onClick={() => removeNewLocation(loc.key)}>
                        Remove
                      </Button>
                    )}
                  </div>

                  {/* #166/#157: match this location to a Google place. Fills exact
                      coordinates and opening hours — the data that otherwise only
                      arrives through admin curation, which private restaurants
                      never receive. Nothing is written until the diff is confirmed. */}
                  <div className="mb-3 p-2 rounded" style={{ background: 'var(--dynerz-bg-light, #E8F0F5)' }}>
                    <Form.Label className="small fw-semibold mb-1">
                      Match to a Google place{' '}
                      {loc.google_place_id && <Badge bg="success" className="ms-1">Linked</Badge>}
                    </Form.Label>
                    <Form.Control
                      type="text"
                      size="sm"
                      placeholder="Search by name, e.g. Whole Foods Market Domain"
                      value={placeSearch(loc.key).query}
                      onChange={(e) => searchPlaces(loc.key, e.target.value, loc)}
                    />
                    <Form.Text className="text-muted">
                      Fills coordinates and opening hours. You'll see exactly what changes before it applies.
                    </Form.Text>
                    {placeSearch(loc.key).loading && (
                      <div className="small text-muted mt-1">
                        <Spinner animation="border" size="sm" className="me-1" />Searching…
                      </div>
                    )}
                    {placeSearch(loc.key).error && (
                      <div className="small text-danger mt-1">{placeSearch(loc.key).error}</div>
                    )}
                    {placeSearch(loc.key).predictions.length > 0 && (
                      <ListGroup className="mt-1">
                        {placeSearch(loc.key).predictions.slice(0, 5).map((p) => (
                          <ListGroup.Item
                            key={p.place_id}
                            action
                            type="button"
                            className="py-1 small"
                            onClick={() => stagePlaceMatch(loc.key, p.place_id, p.structured_formatting?.main_text || p.description)}
                          >
                            {p.description}
                          </ListGroup.Item>
                        ))}
                      </ListGroup>
                    )}
                  </div>

                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Location name (optional)</Form.Label>
                        <Form.Control type="text" value={loc.name} onChange={(e) => updateLocation(loc.key, 'name', e.target.value)} placeholder="e.g. Downtown" />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Phone</Form.Label>
                        <Form.Control type="tel" value={loc.phone} onChange={(e) => updateLocation(loc.key, 'phone', e.target.value)} placeholder="(555) 123-4567" />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Form.Group className="mb-3">
                    <Form.Label>Street Address</Form.Label>
                    <Form.Control type="text" value={loc.address} onChange={(e) => updateLocation(loc.key, 'address', e.target.value)} placeholder="123 Main Street" />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Unit/Suite/Apt</Form.Label>
                    <Form.Control type="text" value={loc.unit} onChange={(e) => updateLocation(loc.key, 'unit', e.target.value)} placeholder="Suite 100" />
                  </Form.Group>

                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>City <span className="text-danger">*</span></Form.Label>
                        <Form.Control type="text" value={loc.city} onChange={(e) => updateLocation(loc.key, 'city', e.target.value)} required />
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group className="mb-3">
                        <Form.Label>State/Region</Form.Label>
                        <Form.Control type="text" value={loc.state} onChange={(e) => updateLocation(loc.key, 'state', e.target.value)} />
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group className="mb-3">
                        <Form.Label>Postal/ZIP</Form.Label>
                        <Form.Control type="text" value={loc.zip_code} onChange={(e) => updateLocation(loc.key, 'zip_code', e.target.value)} />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-0">
                        <Form.Label>Country <span className="text-danger">*</span></Form.Label>
                        <Form.Select value={loc.country} onChange={(e) => updateLocation(loc.key, 'country', e.target.value)} required>
                          {COUNTRIES.map((c) => (
                            <option key={c} value={c}>{c}</option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            ))}

            <h5 className="mb-3 mt-4">Photos</h5>
            <p className="text-muted small">Photos save immediately (not with the button below). The first one becomes the primary photo.</p>
            {imageError && <Alert variant="danger" dismissible onClose={() => setImageError('')}>{imageError}</Alert>}

            {images.length > 0 && (
              <Row className="g-3 mb-3">
                {images.map((img) => (
                  <Col xs={6} md={3} key={img.id}>
                    <Card className="h-100">
                      <div style={{ position: 'relative' }}>
                        <Card.Img variant="top" src={img.flickr_url} alt={img.caption || 'Restaurant photo'} style={{ height: 120, objectFit: 'cover' }} />
                        {Boolean(img.is_primary) && (
                          <Badge bg="primary" style={{ position: 'absolute', top: 6, left: 6 }}>Primary</Badge>
                        )}
                      </div>
                      <Card.Body className="p-2">
                        {img.caption && <div className="small text-muted text-truncate mb-1">{img.caption}</div>}
                        {!Boolean(img.is_primary) && (
                          <Button variant="outline-secondary" size="sm" className="w-100" type="button" disabled={imageBusy} onClick={() => handleSetPrimary(img.id)}>
                            Set as primary
                          </Button>
                        )}
                      </Card.Body>
                    </Card>
                  </Col>
                ))}
              </Row>
            )}

            <Card className="border mb-3">
              <Card.Body>
                <Row className="align-items-end g-2">
                  <Col md={5}>
                    <Form.Label className="small mb-1">Caption (optional)</Form.Label>
                    <Form.Control type="text" value={newCaption} onChange={(e) => setNewCaption(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }} placeholder="e.g. Storefront" disabled={imageBusy} />
                  </Col>
                  <Col md={3}>
                    <Form.Label className="small mb-1">Type</Form.Label>
                    <Form.Select value={newImageType} onChange={(e) => setNewImageType(e.target.value)} disabled={imageBusy}>
                      {IMAGE_TYPES.map((t) => (
                        <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>
                      ))}
                    </Form.Select>
                  </Col>
                  <Col md={4}>
                    <Form.Label className="small mb-1">Location</Form.Label>
                    <Form.Select value={newImageLocationId} onChange={(e) => setNewImageLocationId(e.target.value)} disabled={imageBusy}>
                      <option value="">All locations</option>
                      {locations.filter((l) => l.id != null).map((l) => (
                        <option key={l.id} value={l.id}>{l.name || l.city}</option>
                      ))}
                    </Form.Select>
                  </Col>
                </Row>
                <Row className="align-items-end g-2 mt-1">
                  <Col md={6}>
                    <Form.Label className="small mb-1">Add a photo</Form.Label>
                    <Form.Control type="file" accept="image/*" onChange={handleImageUpload} disabled={imageBusy} />
                  </Col>
                </Row>
                {imageBusy && (
                  <div className="small text-muted mt-2">
                    <Spinner animation="border" size="sm" /> Uploading…
                  </div>
                )}
              </Card.Body>
            </Card>

            <h5 className="mb-3 mt-4">Categories & Cuisine</h5>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Categories (hold Ctrl/Cmd to select multiple)</Form.Label>
                  <Form.Select multiple name="category_ids" value={formData.category_ids} onChange={(e) => handleMultiSelect(e, 'category_ids')} size={5}>
                    {categories.map((category) => (
                      <option key={category.id} value={category.id}>{category.name}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Cuisines (hold Ctrl/Cmd to select multiple)</Form.Label>
                  <Form.Select multiple name="cuisine_ids" value={formData.cuisine_ids} onChange={(e) => handleMultiSelect(e, 'cuisine_ids')} size={5}>
                    {cuisines.map((cuisine) => (
                      <option key={cuisine.id} value={cuisine.id}>{cuisine.name}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <div className="d-flex gap-2 mt-4">
              <Button variant="primary" type="submit" disabled={loading}>
                {loading ? 'Saving…' : 'Save Changes'}
              </Button>
              <Button variant="secondary" onClick={() => navigate(`/restaurant/${id}`)}>Cancel</Button>
            </div>
          </Form>
        </Card.Body>
      </Card>

      {/* #166/#157: confirm before applying a Google match. Google can return a
          relocated or otherwise incorrect place (the Cafe Luna case), so the
          owner sees exactly what would change and can back out. Nothing is
          written to the location until "Apply these changes". */}
      <Modal show={Boolean(pendingMatch)} onHide={() => setPendingMatch(null)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title className="h5">Apply details from Google?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {pendingMatch && (
            <>
              <p className="mb-2">
                Matched <strong>{pendingMatch.label}</strong>.
              </p>
              {pendingMatch.changes.length === 0 ? (
                <Alert variant="info" className="mb-0">
                  Nothing would change — this location already matches that place.
                  Applying will still link it so future refreshes are exact.
                </Alert>
              ) : (
                <>
                  <Table size="sm" responsive className="mb-2">
                    <thead>
                      <tr>
                        <th style={{ width: '20%' }}>Field</th>
                        <th style={{ width: '40%' }}>Current</th>
                        <th style={{ width: '40%' }}>New</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pendingMatch.changes.map((c) => (
                        <tr key={c.field}>
                          <td className="text-muted small">{c.label}</td>
                          <td className="small">
                            {c.from === null || c.from === '' ? <em className="text-muted">empty</em> : String(c.from)}
                          </td>
                          <td className="small fw-semibold">
                            {c.to === null || c.to === '' ? <em className="text-muted">empty</em> : String(c.to)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                  <Alert variant="warning" className="py-2 small mb-0">
                    Check the address matches the place you meant — Google sometimes
                    returns a relocated or similarly-named business.
                  </Alert>
                </>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setPendingMatch(null)}>Cancel</Button>
          <Button variant="primary" onClick={applyPendingMatch}>Apply these changes</Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}

export default EditRestaurantForm;
